import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import type { QuestionSettings } from "@/types/question";
import { DateRangeType } from "@/types/question";
import {
	dateOnlyToLocalMidnight,
	formatDateToLocalYYYYMMDD,
	validateDateInput,
} from "../../utils";

type DateSettingsProps = {
	settings: QuestionSettings;
	setSettings: (settings: QuestionSettings) => void;
	setError: (error: string | null) => void;
};

export const DateSettings = ({
	settings,
	setSettings,
	setError,
}: DateSettingsProps) => {
	const handleDateRangeTypeChange = (key: React.Key | null) => {
		const newDateRangeType = Number(key) as DateRangeType;
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		if (newDateRangeType === DateRangeType.Range) {
			setSettings({
				...settings,
				dateRangeType: newDateRangeType,
				minDate: undefined,
				maxDate: undefined,
			});
			return;
		}

		let newMinDate = settings.minDate;
		let newMaxDate = settings.maxDate;

		if (newDateRangeType === DateRangeType.PastOnly) {
			if (newMinDate && newMinDate > today) newMinDate = undefined;
			if (newMaxDate && newMaxDate > today) newMaxDate = undefined;
		} else if (newDateRangeType === DateRangeType.FutureOnly) {
			if (newMinDate && newMinDate < today) newMinDate = undefined;
			if (newMaxDate && newMaxDate < today) newMaxDate = undefined;
		} else {
			newMinDate = undefined;
			newMaxDate = undefined;
		}

		setSettings({
			...settings,
			dateRangeType: newDateRangeType,
			minDate: newMinDate,
			maxDate: newMaxDate,
		});
	};

	return (
		<div className="rounded-lg border border-gray-200 bg-white p-6">
			<h3 className="mb-4 font-semibold text-gray-900 text-lg">
				Configurações de Data
			</h3>

			<div className="space-y-4">
				<div>
					<label className="mb-2 block font-medium text-gray-700 text-sm">
						Tipo de restrição de data
					</label>
					<Select
						onSelectionChange={handleDateRangeTypeChange}
						placeholder="Selecione o tipo de restrição"
						selectedKey={settings.dateRangeType?.toString() ?? "0"}
					>
						<Select.Item id="0">Qualquer data</Select.Item>
						<Select.Item id="1">Apenas passado</Select.Item>
						<Select.Item id="2">Apenas futuro</Select.Item>
						<Select.Item id="3">Intervalo específico</Select.Item>
					</Select>
				</div>

				{settings.dateRangeType === DateRangeType.Range && (
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label className="mb-2 block font-medium text-gray-700 text-sm">
								Data mínima
							</label>
							<Input
								isInvalid={
									settings.minDate && settings.maxDate
										? settings.minDate > settings.maxDate
										: false
								}
								onChange={(value) => {
									const today = new Date();
									today.setHours(0, 0, 0, 0);

									const newMinDate = validateDateInput({
										value,
										dateRangeType: settings.dateRangeType,
										today,
										existingMaxDate: settings.maxDate,
										setError,
									});

									setSettings({ ...settings, minDate: newMinDate });
								}}
								type="date"
								value={
									settings.minDate
										? formatDateToLocalYYYYMMDD(
												dateOnlyToLocalMidnight(new Date(settings.minDate))
											)
										: ""
								}
							/>
							{settings.minDate &&
								settings.maxDate &&
								settings.minDate > settings.maxDate && (
									<p className="mt-1 text-red-600 text-xs">
										A data mínima não pode ser maior que a data máxima.
									</p>
								)}
						</div>

						<div>
							<label className="mb-2 block font-medium text-gray-700 text-sm">
								Data máxima
							</label>
							<Input
								isInvalid={
									settings.minDate && settings.maxDate
										? settings.maxDate < settings.minDate
										: false
								}
								onChange={(value) => {
									const today = new Date();
									today.setHours(0, 0, 0, 0);

									const newMaxDate = validateDateInput({
										value,
										dateRangeType: settings.dateRangeType,
										today,
										existingMinDate: settings.minDate,
										setError,
									});

									setSettings({ ...settings, maxDate: newMaxDate });
								}}
								type="date"
								value={
									settings.maxDate
										? formatDateToLocalYYYYMMDD(
												dateOnlyToLocalMidnight(new Date(settings.maxDate))
											)
										: ""
								}
							/>
							{settings.minDate &&
								settings.maxDate &&
								settings.maxDate < settings.minDate && (
									<p className="mt-1 text-red-600 text-xs">
										A data máxima não pode ser menor que a data mínima.
									</p>
								)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
